/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';

import {RESIZE_EVENT} from '../../../app.constants';

import {Breadcrumb, BreadcrumbsItem, OutBreadcrumb} from './breadcrumbs.interfaces';

@Component({
  selector: 'gp-tree-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.scss']
})

export class Breadcrumbs implements OnChanges {

  @Input() public selectedItem: BreadcrumbsItem;
  @Input() public items: BreadcrumbsItem[] = [];
  @Input() public breadcrumbsMap: (breadcrumbs: Breadcrumb[]) => Breadcrumb[];

  @Output() public breadcrumbSelected: EventEmitter<OutBreadcrumb> = new EventEmitter<OutBreadcrumb>();

  public breadcrumbs: Breadcrumb[] = [];
  private itemsMap: Map<number, Breadcrumb> = new Map<number, Breadcrumb>();

  constructor() {
  }

  public ngOnChanges(changes: SimpleChanges): void {
    setTimeout(() => window.dispatchEvent(RESIZE_EVENT));

    if (changes.items) {
      this.fetchBreadcrumbs();
      this.createBreadcrumbs();
    }

    if (changes.selectedItem) {
      this.createBreadcrumbs();
    }
  }

  public setDropDownStyle(dropdown: HTMLElement): void {
    dropdown.style.left = '10px';
    setTimeout(() => {
      const windowWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);
      const dropdownRight = dropdown.getBoundingClientRect().right;
      if (windowWidth < dropdownRight) {
        dropdown.style.left = (windowWidth - dropdownRight - 10) + 'px';
      }
    });
  }

  public selectBreadcrumb(breadcrumb: Breadcrumb): void {
    this.breadcrumbSelected.emit({
      id: breadcrumb.id,
      parent: breadcrumb.parent,
      name: breadcrumb.name,
      type: breadcrumb.type
    });
  }

  private fetchBreadcrumbs(): void {
    if (!this.items) {
      return;
    }

    this.itemsMap.clear();
    this.items.forEach((item: BreadcrumbsItem) => {
      const foundItem: Breadcrumb = this.itemsMap.get(item.id);
      const foundParent: Breadcrumb = this.itemsMap.get(item.parent);

      if (foundItem) {
        foundItem.name = item.name;
        foundItem.parent = item.parent;
      } else {
        this.itemsMap.set(item.id, this.getDefaultBreadcrumb(item.id, item.name, item.parent));
      }

      if (foundParent) {
        foundParent.children.push(this.itemsMap.get(item.id));
      } else {
        const newItem: Breadcrumb = this.getDefaultBreadcrumb(item.parent);
        newItem.children.push(this.itemsMap.get(item.id));
        this.itemsMap.set(item.parent, newItem);
      }
    });
  }

  private createBreadcrumbs(): void {
    this.breadcrumbs = [];
    if (this.selectedItem) {
      let pushElement = this.itemsMap.get(this.selectedItem.id);
      while (pushElement && pushElement.id) {
        this.breadcrumbs.unshift(pushElement);
        pushElement = this.itemsMap.get(pushElement.parent);
      }
    }

    if (this.breadcrumbsMap) {
      this.breadcrumbs = this.breadcrumbsMap(this.breadcrumbs);
    }
  }

  private getDefaultBreadcrumb(id: number, name?: string, parent?: number): Breadcrumb {
    return {
      parent,
      id,
      name,
      children: [],
      opened: false,
      type: 'item'
    };
  }
}
